/**
 * Canonical employee display-name helpers (batch-2 Item 6, AC-34/AC-36/AC-39).
 *
 * Mirrors the backend resolver (`common/employee-identity.util.ts`): the HiBob
 * "Preferred name" is a FULL display string stored on `Employee.displayName` —
 * never split it into first/last parts. Every name render and every
 * search/picker haystack routes through here so a preferred name shows (and
 * matches) everywhere, and avatar initials derive from the same string the
 * label shows.
 */

/** Structural ref — every employee-ish shape in the app satisfies it. */
export interface EmployeeNameRef {
  firstName?: string | null;
  lastName?: string | null;
  /** Preferred full display string (HiBob "Preferred name"). */
  displayName?: string | null;
  email?: string | null;
}

const legalFullName = (ref: EmployeeNameRef): string =>
  `${ref.firstName ?? ''} ${ref.lastName ?? ''}`.trim();

/** The string to render: non-blank preferred name, else `First Last`. */
export function employeeDisplayName(ref: EmployeeNameRef): string {
  const preferred = (ref.displayName ?? '').trim();
  return preferred || legalFullName(ref);
}

/**
 * MUI Autocomplete `filterOptions`-shaped predicate: keeps options whose
 * preferred OR legal name (or `extraText`, e.g. the position shown in the
 * label) contains the query (AC-36). Use as
 * `filterOptions={(opts, s) => filterEmployeeOptions(opts, s.inputValue, ...)}`.
 */
export function filterEmployeeOptions<T extends EmployeeNameRef>(
  options: T[],
  inputValue: string,
  extraText?: (option: T) => string | null | undefined,
): T[] {
  const term = inputValue.trim().toLowerCase();
  if (!term) return options;
  return options.filter((o) =>
    `${employeeNameHaystack(o)} ${(extraText?.(o) ?? '').toLowerCase()}`.includes(term),
  );
}

/**
 * Lower-cased search haystack matching BOTH the preferred and the legal name
 * (plus the email when the ref carries one). Deduplicated so a preferred name
 * equal to the legal name adds no noise (AC-E8). Callers filter with
 * `employeeNameHaystack(e).includes(query.trim().toLowerCase())`.
 */
export function employeeNameHaystack(ref: EmployeeNameRef): string {
  const parts = [
    (ref.displayName ?? '').trim(),
    legalFullName(ref),
    (ref.email ?? '').trim(),
  ]
    .filter(Boolean)
    .map((part) => part.toLowerCase());
  return [...new Set(parts)].join(' ');
}
